import { useMemo, useCallback } from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult
} from "@hello-pangea/dnd";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { ShowingType, ShowingRotationSlot } from "api/scheduling/types";
import { schedulingApi } from "api/scheduling/api";
import { Card } from "shared/components/Card";
import { Loader } from "shared/components/Loader";
import { Show } from "shared/components";
import { RotationUser } from "./types";
import { RotationUserCard } from "./RotationUserCard";
import { PausedRotationUserCard } from "./PausedRotationUserCard";

const TITLE_MAP: Record<ShowingType, string> = {
  [ShowingType.SelfGuided]: "Self-guided showing round robin",
  [ShowingType.Guided]: "Guided showing round robin"
};

const ROTATION_KEY = "SHOWING_ROTATION";

const reorder = <T,>(list: T[], startIndex: number, endIndex: number): T[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const mapSlotToUser = (slot: ShowingRotationSlot): RotationUser => ({
  id: slot.partner_user_id,
  name: slot.name,
  showings_today: slot.showings_today,
  showings_this_week: slot.showings_this_week,
  is_next: slot.is_next,
  paused: slot.paused,
  pause_reason: slot.pause_reason
});

interface RotationListProps {
  propertyId: string;
  showingType: ShowingType;
}

export const RotationList = ({
  propertyId,
  showingType
}: RotationListProps) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => [ROTATION_KEY, propertyId], [propertyId]);

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => schedulingApi.getRotation(propertyId),
    enabled: !!propertyId
  });

  const users = useMemo(() => (data ?? []).map(mapSlotToUser), [data]);
  const activeUsers = useMemo(() => users.filter((u) => !u.paused), [users]);
  const pausedUsers = useMemo(() => users.filter((u) => u.paused), [users]);

  const invalidateRotation = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  const reorderMutation = useMutation({
    mutationKey: [ROTATION_KEY, propertyId, "UPDATE"],
    mutationFn: (partnerUserIds: number[]) =>
      schedulingApi.reorderRotation(propertyId, partnerUserIds),
    onMutate: async (partnerUserIds) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<ShowingRotationSlot[]>(queryKey);

      queryClient.setQueryData<ShowingRotationSlot[]>(queryKey, (old) => {
        if (!old) return old;
        const byId = new Map(old.map((s) => [s.partner_user_id, s]));
        const reordered = partnerUserIds
          .map((id) => byId.get(id))
          .filter(Boolean) as ShowingRotationSlot[];
        return reordered.map((s, i) => ({
          ...s,
          position: i + 1
        }));
      });

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);

      toast.error("Failed to reorder rotation");
    },
    onSettled: invalidateRotation
  });

  const setNextMutation = useMutation({
    mutationKey: [ROTATION_KEY, propertyId, "SET_NEXT"],
    mutationFn: (partnerUserId: number) =>
      schedulingApi.setNextRotationUser(propertyId, partnerUserId),
    onError: () => toast.error("Failed to set next user"),
    onSettled: invalidateRotation
  });

  const buildReorderPayload = useCallback(
    (reorderedActive: RotationUser[]) =>
      [...reorderedActive, ...pausedUsers].map((u) => u.id),
    [pausedUsers]
  );

  const onDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      if (result.source.index === result.destination.index) return;

      const reordered = reorder(
        activeUsers,
        result.source.index,
        result.destination.index
      );
      reorderMutation.mutate(buildReorderPayload(reordered));
    },
    [activeUsers, reorderMutation, buildReorderPayload]
  );

  const handleSetNext = (id: number) => setNextMutation.mutate(id);

  const handleMoveUp = useCallback(
    (index: number) => {
      if (index === 0) return;
      const reordered = reorder(activeUsers, index, index - 1);
      reorderMutation.mutate(buildReorderPayload(reordered));
    },
    [activeUsers, reorderMutation, buildReorderPayload]
  );

  const handleMoveDown = useCallback(
    (index: number) => {
      if (index >= activeUsers.length - 1) return;
      const reordered = reorder(activeUsers, index, index + 1);
      reorderMutation.mutate(buildReorderPayload(reordered));
    },
    [activeUsers, reorderMutation, buildReorderPayload]
  );

  if (isLoading)
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <Loader />
        </div>
      </Card>
    );

  return (
    <Card>
      <div className="px-4 md:px-8 py-4 md:py-6 border-b border-alpha-40">
        <h3 className="text-base font-bold text-content-1000">
          {TITLE_MAP[showingType]}
        </h3>
        <p className="mt-2 text-sm text-content-600 font-medium">
          A round robin algorithm is used as a tiebreaker when multiple leasing
          agents are available at the same time for a given showing slot. Lead
          ownership is checked first. The list of users below does not
          necessarily reflect today&rsquo;s availability.
        </p>
      </div>

      <div className="px-4 md:px-8 py-4 md:py-6 space-y-4">
        <p className="text-sm text-content-200 font-medium">
          Active in rotation &middot; {activeUsers.length}{" "}
          {activeUsers.length === 1 ? "user" : "users"}
        </p>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="rotation-list">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="flex flex-col gap-4"
              >
                {activeUsers.map((user, i) => (
                  <Draggable
                    key={user.id}
                    draggableId={String(user.id)}
                    index={i}
                  >
                    {(provided, snapshot) => (
                      <RotationUserCard
                        ref={provided.innerRef}
                        user={user}
                        index={i}
                        totalUsers={activeUsers.length}
                        draggableProps={provided.draggableProps}
                        dragHandleProps={provided.dragHandleProps}
                        isDragging={snapshot.isDragging}
                        onSetNext={handleSetNext}
                        onMoveUp={handleMoveUp}
                        onMoveDown={handleMoveDown}
                      />
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <Show if={pausedUsers.length > 0}>
          <p className="text-sm text-content-200 font-medium pt-2">
            Paused &middot; {pausedUsers.length}{" "}
            {pausedUsers.length === 1 ? "user" : "users"}
          </p>

          <div className="flex flex-col gap-4">
            {pausedUsers.map((user) => (
              <PausedRotationUserCard key={user.id} user={user} />
            ))}
          </div>
        </Show>
      </div>
    </Card>
  );
};
